/**
 * API calls for the labelling surface — every rating and tagging study (spec 37).
 *
 * One client for all of them: a study's instrument and its items' render kinds come back as data,
 * so adding a study needs no change here.
 *
 * Auth is the app's cookie session, so every request carries `credentials: 'include'`.
 */

const API_URL = import.meta.env.VITE_API_URL || '';

export const HTTP_FORBIDDEN = 403;
/** The unit's labels are already filled by other raters — this rater's claim had lapsed. */
export const HTTP_CONFLICT = 409;
const HTTP_NO_CONTENT = 204;

/**
 * A failed request, carrying the status so callers can tell the failures apart.
 *
 * A plain Error forces every caller to match on message text, and the difference that matters here
 * is not cosmetic: a 409 means this item is gone for good and the rater must be moved on, while
 * every other failure means "try again". Reading that off a string is how the two get confused.
 */
export class LabellingApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'LabellingApiError';
    this.status = status;
  }
}

/**
 * Where a simulation item's report is read from.
 *
 * Scoped to the ASSIGNMENT and the item's position in its unit, never to `item_ref`. `item_ref` is
 * the id of the thing being judged (a simulation id); holding the assignment is the only thing that
 * authorises a rater to read it, since they own none of the content in a study.
 */
export const simulationItemPath = (assignmentId: number, index: number) =>
  `/labelling/assignments/${assignmentId}/items/${index}/simulation`;

/** The four instrument types a study may use. Mirrors services_fastapi/labelling/instruments.py. */
export type InstrumentType = 'scalar' | 'rubric' | 'choice' | 'tags';

export interface Scale {
  min: number;
  max: number;
  labels?: Record<string, string>;
}

export interface Instrument {
  type: InstrumentType;
  /** scalar */
  scale?: Scale;
  /** rubric */
  dimensions?: { key: string; label: string; scale: Scale }[];
  /** tags */
  vocabulary?: { key: string; label: string }[];
  min?: number;
  max?: number;
  remarks?: 'required' | 'optional';
}

export interface StudySummary {
  slug: string;
  title: string;
  instructions: string | null;
  instrument_type: InstrumentType;
  status: string;
  /** This user's own progress — never anyone else's. */
  total: number;
  answered: number;
}

export interface StudyDetail {
  slug: string;
  title: string;
  instructions: string | null;
  instrument: Instrument;
  instrument_version: number;
  unit_size: number;
  status: string;
}

export interface ItemSummary {
  id: number;
  item_ref: string;
  render_kind: string;
  title: string | null;
}

export interface Assignment {
  id: number;
  unit_id: number;
  status: string;
  items: ItemSummary[];
  /** This user's OWN answer, shaped by the study's instrument. */
  response: Record<string, unknown> | null;
  remarks: string | null;
  is_answered: boolean;
}

export interface ItemPayload {
  id: number;
  item_ref: string;
  render_kind: string;
  title: string | null;
  payload: Record<string, unknown> | null;
  payload_uri: string | null;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });

  if (response.status === HTTP_FORBIDDEN) {
    throw new LabellingApiError(HTTP_FORBIDDEN, 'You do not have access to this study.');
  }
  if (!response.ok) {
    // Surface the backend's own message where it has one — it explains exactly why a response was
    // rejected (score out of range, a rubric dimension missing, remarks required, the slot gone).
    let detail = '';
    try {
      detail = (await response.json())?.detail ?? '';
    } catch {
      /* non-JSON error body */
    }
    throw new LabellingApiError(response.status, detail || `Request failed (${response.status})`);
  }
  // 204 = nothing left in this study for this rater.
  if (response.status === HTTP_NO_CONTENT) return null as T;
  return response.json();
}

/** The studies this user may rate. Filtered server-side; a study they cannot open is absent. */
export const fetchStudies = () =>
  request<{ studies: StudySummary[] }>('/labelling/studies').then((r) => r.studies);

export const fetchStudy = (slug: string) =>
  request<StudyDetail>(`/labelling/studies/${encodeURIComponent(slug)}`);

/** Advance to the next unit needing judgement. Null when the study is finished for this rater. */
export const fetchNextAssignment = (slug: string) =>
  request<{ id: number } | null>(`/labelling/studies/${encodeURIComponent(slug)}/next`, {
    method: 'POST',
  });

export const fetchQueue = (slug: string) =>
  request<{ assignments: Assignment[] }>(
    `/labelling/studies/${encodeURIComponent(slug)}/queue`,
  ).then((r) => r.assignments);

/** One item's body — fetched per item because a payload can be ~1 MB. */
export const fetchItem = (assignmentId: number, index: number) =>
  request<ItemPayload>(`/labelling/assignments/${assignmentId}/items/${index}`);

/** Save or revise this rater's answer. Idempotent, so editing later is the same call. */
export const saveResponse = (
  assignmentId: number,
  body: { response: Record<string, unknown>; remarks: string },
) =>
  request<{ id: number; instrument_version: number }>(
    `/labelling/assignments/${assignmentId}/response`,
    { method: 'PUT', body: JSON.stringify(body) },
  );
